export type AdminSection = {
  id: string;
  navLabel: string;
  title: string;
  path: string;
  description: string;
  statusLabel: string;
  statusTone: 'primary' | 'success' | 'warning' | 'info';
  summaryCards: Array<{
    label: string;
    value: string;
    tone: 'primary' | 'success' | 'warning' | 'info';
  }>;
  availableNow: string[];
  unavailableHere: string[];
  actionLinks: Array<{
    label: string;
    path?: string;
    status: string;
    tone: 'primary' | 'success' | 'warning' | 'info';
  }>;
};

export const adminSections: AdminSection[] = [
  {
    id: 'overview',
    navLabel: 'Overview',
    title: 'Admin Overview',
    path: '/admin/overview',
    description: 'Platform control surface for the admin tools that are live now: tenant management, admin access, and system theme defaults.',
    statusLabel: 'Admin tools live',
    statusTone: 'success',
    summaryCards: [
      { label: 'Tenant CRUD', value: 'Live', tone: 'success' },
      { label: 'Admin access', value: 'Live', tone: 'success' },
      { label: 'Theme defaults', value: 'Live', tone: 'success' },
    ],
    availableNow: [
      'Create, edit, and review customer records in Clients.',
      'Grant or revoke platform-admin access in Platform Admin Users & Access.',
      'Save shared shell theme defaults in Theme Options.',
    ],
    unavailableHere: [
      'This overview route does not create invoices, manage plans, or provide support intervention tools.',
      'Use the linked live modules for real admin work instead of treating this page as an action surface.',
    ],
    actionLinks: [
      { label: 'Open Clients', path: '/admin/clients', status: 'Live now', tone: 'success' },
      { label: 'Open Platform Admin Users & Access', path: '/admin/users-access', status: 'Live now', tone: 'success' },
      { label: 'Open Theme Options', path: '/admin/system-settings/theme-options', status: 'Live now', tone: 'success' },
      { label: 'Open Audit / Activity', path: '/admin/audit-activity', status: 'Live now', tone: 'success' },
    ],
  },
  {
    id: 'clients',
    navLabel: 'Clients',
    title: 'Clients',
    path: '/admin/clients',
    description: 'Prepare the tenant registry for onboarding, status control, and company setup across the CARES platform.',
    statusLabel: 'CRUD live',
    statusTone: 'success',
    summaryCards: [
      { label: 'Tenant registry', value: 'Company records', tone: 'info' },
      { label: 'Status controls', value: 'Live', tone: 'success' },
      { label: 'Ownership model', value: 'Tenant-first', tone: 'success' },
    ],
    availableNow: [
      'Create, edit, and remove tenant records from one platform-admin workspace.',
      'Expose onboarding status, seeded readiness, and deletion blockers.',
      'Treat tenant data as the parent context for customer configuration.',
    ],
    unavailableHere: [],
    actionLinks: [],
  },
  {
    id: 'users-access',
    navLabel: 'Platform Admin Users & Access',
    title: 'Platform Admin Users & Access',
    path: '/admin/users-access',
    description: 'Manage platform admins, tenant admins, scheduler admins, invites, and scoped role assignments.',
    statusLabel: 'Platform admin live',
    statusTone: 'success',
    summaryCards: [
      { label: 'Platform admin table', value: 'Live', tone: 'success' },
      { label: 'Scoped permissions', value: 'Enabled', tone: 'success' },
      { label: 'Invite workflow', value: 'Email-based', tone: 'primary' },
    ],
    availableNow: [
      'Show role scope clearly: platform, tenant, department, and self.',
      'Use audit-friendly controls for grants and status changes.',
      'Avoid exposing high-scope actions to tenant-only operators.',
    ],
    unavailableHere: [],
    actionLinks: [],
  },
  {
    id: 'accounting',
    navLabel: 'Accounting / Financials',
    title: 'Accounting / Financials',
    path: '/admin/accounting',
    description: 'This route is an operator summary only. CARES does not ship invoice, payment, or subscription controls here today.',
    statusLabel: 'No live accounting tools',
    statusTone: 'warning',
    summaryCards: [
      { label: 'Invoices', value: 'Not available', tone: 'warning' },
      { label: 'Payment status', value: 'Not available', tone: 'warning' },
      { label: 'Contract visibility', value: 'Not available', tone: 'warning' },
    ],
    availableNow: [
      'Use Clients to review customer identity, status, and onboarding context.',
      'Use Platform Admin Users & Access for administrator access changes related to account ownership.',
      'Use Audit / Activity to review live audit events and guarded break-glass session state.',
    ],
    unavailableHere: [
      'No invoice list, payment ledger, collections workflow, or dispute tooling is live on this route.',
      'There is no separate live finance module elsewhere in CARES today.',
    ],
    actionLinks: [
      { label: 'Open Clients', path: '/admin/clients', status: 'Live now', tone: 'success' },
      { label: 'Open Platform Admin Users & Access', path: '/admin/users-access', status: 'Live now', tone: 'success' },
      { label: 'Finance module', status: 'No live route yet', tone: 'warning' },
    ],
  },
  {
    id: 'billing-config',
    navLabel: 'Plans / Billing Configuration',
    title: 'Plans / Billing Configuration',
    path: '/admin/billing-config',
    description: 'This route does not manage plans, pricing, or entitlements today. It exists to state that limitation clearly.',
    statusLabel: 'No live billing configuration',
    statusTone: 'warning',
    summaryCards: [
      { label: 'Plan catalog', value: 'Not available', tone: 'warning' },
      { label: 'Entitlements', value: 'Not available', tone: 'warning' },
      { label: 'Price controls', value: 'Not available', tone: 'warning' },
    ],
    availableNow: [
      'Use Clients to inspect which customer record an eventual billing model would attach to.',
      'Use Theme Options only for shell defaults; it does not define commercial plans.',
      'Use Platform Admin Users & Access for current admin permission changes.',
    ],
    unavailableHere: [
      'No plan catalog, feature entitlement editor, subscription mapping, or pricing controls are live here.',
      'There is no other CARES route that currently exposes billing configuration tools.',
    ],
    actionLinks: [
      { label: 'Open Clients', path: '/admin/clients', status: 'Live now', tone: 'success' },
      { label: 'Open Theme Options', path: '/admin/system-settings/theme-options', status: 'Live now', tone: 'success' },
      { label: 'Billing configuration tools', status: 'No live route yet', tone: 'warning' },
    ],
  },
  {
    id: 'theme-options',
    navLabel: 'Theme Options',
    title: 'Theme Options',
    path: '/admin/system-settings/theme-options',
    description: 'Provide app-wide controls for platform behavior, theme defaults, and environment-aware visual toggles.',
    statusLabel: 'Persistent defaults',
    statusTone: 'success',
    summaryCards: [
      { label: 'Route guard', value: 'Active', tone: 'success' },
      { label: 'Theme defaults', value: 'Persistent', tone: 'success' },
      { label: 'Regular-user scope', value: 'Light / dark only', tone: 'primary' },
    ],
    availableNow: [
      'Keep broad shell/theme controls limited to platform administrators.',
      'Expose saved defaults that every authenticated shell can inherit on load.',
      'Preserve a narrow light/dark toggle for regular users without surfacing the full switcher.',
    ],
    unavailableHere: [],
    actionLinks: [],
  },
  {
    id: 'audit-activity',
    navLabel: 'Audit / Activity',
    title: 'Audit / Activity',
    path: '/admin/audit-activity',
    description: 'Centralize admin actions, security-sensitive changes, and operational review trails.',
    statusLabel: 'Audit review live',
    statusTone: 'success',
    summaryCards: [
      { label: 'Global audit perm', value: 'Seeded', tone: 'success' },
      { label: 'Event timeline', value: 'Live', tone: 'success' },
      { label: 'Break-glass controls', value: 'Guarded', tone: 'warning' },
    ],
    availableNow: [
      'Review recent admin audit events by tenant scope without leaving the admin workspace.',
      'Inspect active and current break-glass session state before any high-risk intervention.',
      'Keep support-grade actions distinguishable from tenant edits with actor, target, and outcome context.',
    ],
    unavailableHere: [
      'This route does not provide impersonation, support ticket triage, or unrestricted emergency elevation.',
      'Break-glass activation stays limited to eligible local platform-admin accounts and should only be used with explicit approval.',
    ],
    actionLinks: [],
  },
  {
    id: 'support-tools',
    navLabel: 'Support Tools',
    title: 'Support Tools',
    path: '/admin/support-tools',
    description: 'This route is an operator notice only. CARES does not expose impersonation or support intervention tools here today.',
    statusLabel: 'No live support tools',
    statusTone: 'warning',
    summaryCards: [
      { label: 'Impersonation', value: 'Not available', tone: 'warning' },
      { label: 'Support triage', value: 'Not available', tone: 'warning' },
      { label: 'Audit requirement', value: 'Mandatory before launch', tone: 'primary' },
    ],
    availableNow: [
      'Use Platform Admin Users & Access for current permission changes that do not require impersonation.',
      'Use Clients for customer record review and status updates.',
      'Use Audit / Activity to inspect live audit events and guarded break-glass session status.',
    ],
    unavailableHere: [
      'No impersonation, break-glass session controls, or operator triage queue is live on this page.',
      'Those support interventions do not have another live CARES route today.',
    ],
    actionLinks: [
      { label: 'Open Platform Admin Users & Access', path: '/admin/users-access', status: 'Live now', tone: 'success' },
      { label: 'Open Clients', path: '/admin/clients', status: 'Live now', tone: 'success' },
      { label: 'Support intervention tools', status: 'No live route yet', tone: 'warning' },
    ],
  },
];
